import { moduloDoEndereco } from './modulos';

/**
 * O aplicativo no celular (onda 298 — o app no celular).
 *
 * ── O pedido ─────────────────────────────────────────────────────────
 *
 * «É possível publicar os apps LabManager e Campo como aplicativo de
 * Android para usar nativamente nos celulares dos laboratoristas e
 * sondadores?» — e, das três saídas que a sessão mostrou, o Humberto
 * escolheu a primeira (07/10/2026, «Sim, caminho 1»): o PWA instalado pelo
 * próprio Chrome. Sem Play Store, sem aprovação de ninguém, sem APK
 * solto — que desde 30/09/2026 o Android certificado no Brasil só instala
 * de desenvolvedor verificado.
 *
 * ── Dois aplicativos, uma origem ─────────────────────────────────────
 *
 * O sistema é um só (um banco, um login), mas quem está no laboratório e
 * quem está na sondagem não usam a mesma porta. Por isso há DOIS
 * manifestos, cada um com o seu `id`:
 *
 *   · `/manifest.webmanifest`       — LabManager, `id` `/`. É o manifesto
 *     de sempre, renomeado: o `id` `/` é o que o Chrome já deu a quem
 *     instalou o «Testesolo OS» antes desta onda, e mantê-lo é o que faz
 *     a instalação antiga virar LabManager em vez de ficar órfã;
 *   · `/manifest-campo.webmanifest` — Campo, `id` `/campo`, abre no Campo.
 *
 * O `<link rel="manifest">` da página acompanha o endereço (é ele que o
 * Chrome lê quando a pessoa toca «Instalar app»): a tela do Campo carrega
 * o manifesto do Campo, a do LabManager o do LabManager, e a tela que não
 * decide — a entrada, a raiz, o módulo compartilhado — mantém a escolha
 * guardada em `/instalar/<app>`.
 *
 * ── Os dois ícones ───────────────────────────────────────────────────
 *
 * O LabManager tem marca própria desde a onda 300 — o erlenmeyer sobre o
 * terreno, o arquivo que o Humberto entregou em 07/10
 * (`marca/labmanager-icone.svg` → `icone-labmanager-*`). O Campo ainda não
 * tem a dele («temos que criar», 07/10) e usa o símbolo OFICIAL da
 * Testesolo (`icone-campo-*`, o mesmo recorte da 230). Desenhar um símbolo
 * para o Campo sem a marca aprovada seria inventar marca — a regra mais
 * antiga do projeto (doc 06 §3).
 *
 * O iPhone não lê o ícone do manifesto: lê o `<link rel="apple-touch-icon">`.
 * Por isso o `aplicarManifesto` troca os dois links juntos.
 *
 * ── O que fica no aparelho ───────────────────────────────────────────
 *
 * A fila de envio (`fila.ts`) mora no IndexedDB, e o Android apaga o
 * armazenamento de site quando o celular fica sem espaço — a não ser que
 * o site tenha pedido armazenamento PERSISTENTE e o navegador tenha dado.
 * O Chrome dá sozinho ao app instalado; por isso o pedido sai quando a
 * página abre como aplicativo, logo depois de instalar, e toda vez que um
 * lançamento fica guardado no aparelho.
 *
 * Este arquivo não importa React: as regras puras (`appDoEndereco`,
 * `appDaPagina`) se testam fora do navegador, e o resto só toca no
 * `window` dentro das funções.
 */

export type AppDoCelular = 'labmanager' | 'campo';

export interface DadosDoApp {
  /** O nome embaixo do ícone (o `short_name` do manifesto). */
  nome: string;
  manifesto: string;
  /** Para onde o ícone leva (o caminho do `start_url`, sem a consulta). */
  inicio: string;
  /** O ícone de 192 px (o mesmo do manifesto) — o da página `/instalar`. */
  icone: string;
  /** O ícone que o iPhone usa na tela inicial (`apple-touch-icon`). */
  toque: string;
  /** Uma frase para quem escolhe. */
  para: string;
}

export const APPS: Record<AppDoCelular, DadosDoApp> = {
  labmanager: {
    nome: 'LabManager',
    manifesto: '/manifest.webmanifest',
    inicio: '/',
    icone: '/icone-labmanager-192.png',
    toque: '/apple-touch-icon.png',
    para: 'Para o laboratório: recebimento de amostras, fila, ensaios, '
      + 'estufa e laudos.',
  },
  campo: {
    nome: 'Campo',
    manifesto: '/manifest-campo.webmanifest',
    inicio: '/campo',
    icone: '/icone-campo-192.png',
    toque: '/apple-touch-icon-campo.png',
    para: 'Para a sondagem e o campo: diários, equipes, pontos e leituras '
      + 'de instrumentos — as leituras ficam guardadas quando falta sinal.',
  },
};

export const ehApp = (x: unknown): x is AppDoCelular => x === 'labmanager' || x === 'campo';

/** Onde fica a escolha feita em `/instalar/<app>`. */
export const CHAVE_DO_APP = 'labmanager.app';
/**
 * Qual aplicativo ESTA janela é, quando ela abriu pelo ícone.
 *
 * `sessionStorage`, e não `localStorage`: cada aplicativo instalado é uma
 * janela própria, e os dois dividem o `localStorage` da origem (é por isso
 * que o login vale nos dois). A janela do Campo não pode dizer à do
 * LabManager que ela é o Campo.
 */
export const CHAVE_DA_JANELA = 'labmanager.app-aberto';
/** A consulta que o `start_url` de cada manifesto leva. */
export const ORIGEM_DO_ICONE = 'origem';

/**
 * Que aplicativo o endereço pede — `null` quando o endereço não decide.
 *
 * Decide pelo registro de módulos (o mesmo `moduloDoEndereco` que acha o
 * espaço da tela, `RN-240`), sem olhar a sessão:
 *
 *   · `/instalar/campo` e `/instalar/labmanager` — a escolha explícita;
 *   · `/os/:id/campo` — a aba de campo da OS, que hoje pende do módulo
 *     das OS, é do Campo;
 *   · módulo que mora só no Campo → Campo;
 *   · módulo que mora no Hub ou no LabManager, e não no Campo → LabManager
 *     (o Hub é de quem gere, e quem gere instala o LabManager);
 *   · módulo compartilhado com o Campo (Equipamentos), tela do Sistema, a
 *     raiz, a entrada e endereço desconhecido → não decide.
 */
export function appDoEndereco(onde: string): AppDoCelular | null {
  const escolhido = /^\/instalar\/([a-z]+)\/?$/.exec(onde);
  if (escolhido) return ehApp(escolhido[1]) ? escolhido[1] : null;
  if (/^\/os\/[^/]+\/campo(?:\/|$)/.test(onde)) return 'campo';
  if (onde === '/' || onde === '') return null;
  const m = moduloDoEndereco(onde);
  if (!m) return null;
  const espacos = m.espacos.filter((e) => e !== 'sistema');
  if (espacos.length === 0) return null;
  if (espacos.every((e) => e === 'campo')) return 'campo';
  if (espacos.includes('campo')) return null;
  return 'labmanager';
}

/**
 * O aplicativo cujo manifesto a página deve carregar.
 *
 * Na janela aberta pelo ícone, é o aplicativo dela, sempre — o Chrome
 * confere o manifesto da página para atualizar o aplicativo instalado, e
 * um manifesto de OUTRO `id` ali seria um aplicativo que nunca se
 * atualiza. Fora dela, o endereço decide; se ele não decide, vale a
 * escolha guardada; se não há escolha, o LabManager.
 */
export function appDaPagina(
  onde: string,
  guardado: AppDoCelular | null,
  daJanela: AppDoCelular | null = null,
): AppDoCelular {
  return daJanela ?? appDoEndereco(onde) ?? guardado ?? 'labmanager';
}

// ── O navegador ──────────────────────────────────────────────────────

const ler = (onde: Storage | undefined, chave: string): AppDoCelular | null => {
  try {
    const v = onde?.getItem(chave);
    return ehApp(v) ? v : null;
  } catch { return null; }
};
const sessao = () => (typeof sessionStorage === 'undefined' ? undefined : sessionStorage);
const local = () => (typeof localStorage === 'undefined' ? undefined : localStorage);

export const appGuardado = () => ler(local(), CHAVE_DO_APP);
export const appDaJanela = () => ler(sessao(), CHAVE_DA_JANELA);

export function guardarApp(app: AppDoCelular) {
  try { local()?.setItem(CHAVE_DO_APP, app); } catch { /* janela privada */ }
}

/** A página abriu como aplicativo (pelo ícone), e não numa aba. */
export function rodandoComoApp(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if ((navigator as Navigator & { standalone?: boolean }).standalone === true) return true;
    return ['standalone', 'fullscreen', 'minimal-ui']
      .some((m) => matchMedia(`(display-mode: ${m})`).matches);
  } catch { return false; }
}

/** iPhone e iPad não têm o «Instalar app»: é o Compartilhar do Safari. */
export function ehIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /iPhone|iPad|iPod/.test(ua)
    || (/Macintosh/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1);
}

/**
 * Lê a consulta `?origem=app-<app>` com que o ícone abre a janela, guarda
 * na sessão da janela e tira a consulta do endereço — antes do roteador
 * montar, para ninguém ver nem guardar o endereço com ela.
 */
function lerOrigemDoIcone() {
  if (typeof location === 'undefined') return;
  try {
    const url = new URL(location.href);
    const origem = url.searchParams.get(ORIGEM_DO_ICONE);
    const m = origem ? /^app-([a-z]+)$/.exec(origem) : null;
    if (!m) return;
    if (ehApp(m[1])) sessao()?.setItem(CHAVE_DA_JANELA, m[1]);
    url.searchParams.delete(ORIGEM_DO_ICONE);
    history.replaceState(history.state, '', url.pathname + url.search + url.hash);
  } catch { /* nada */ }
}

/**
 * Troca o `<link rel="manifest">` da página pelo do aplicativo certo — e o
 * `<link rel="apple-touch-icon">` junto (onda 300), que é o que o iPhone
 * põe na tela inicial. Devolve o aplicativo escolhido.
 */
export function aplicarManifesto(onde: string): AppDoCelular {
  const app = appDaPagina(onde, appGuardado(), appDaJanela());
  if (typeof document === 'undefined') return app;
  const trocar = (rel: string, alvo: string) => {
    const link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
    if (link && link.getAttribute('href') !== alvo) link.setAttribute('href', alvo);
  };
  trocar('manifest', APPS[app].manifesto);
  trocar('apple-touch-icon', APPS[app].toque);
  return app;
}

// ── O pedido de instalação ───────────────────────────────────────────

/**
 * O evento que o Chrome dispara quando a página é instalável. Guardado
 * para o botão «Instalar» da página `/instalar` chamar na hora em que a
 * pessoa toca — fora de um toque, o `prompt()` é recusado.
 *
 * Sem `preventDefault()`: o Chrome continua oferecendo a instalação do
 * jeito dele (o menu ⋮ e, em alguns aparelhos, a faixa de baixo). O botão
 * é um caminho a mais, não um que tira os outros.
 */
interface PedidoDeInstalacao extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let pedido: PedidoDeInstalacao | null = null;
let instaladoAgora = false;
const ouvintes = new Set<() => void>();
const avisar = () => ouvintes.forEach((f) => f());

export const podeInstalar = () => pedido !== null;
export const acabouDeInstalar = () => instaladoAgora;

export function aoMudarInstalacao(f: () => void): () => void {
  ouvintes.add(f);
  return () => { ouvintes.delete(f); };
}

/** Abre a janela de instalação do Chrome. `null` quando não há o que abrir. */
export async function instalar(): Promise<'aceito' | 'recusado' | null> {
  const p = pedido;
  if (!p) return null;
  pedido = null;
  await p.prompt();
  const { outcome } = await p.userChoice;
  avisar();
  return outcome === 'accepted' ? 'aceito' : 'recusado';
}

// ── O armazenamento que o Android não apaga ──────────────────────────

export interface Armazenamento {
  /** `null` quando o navegador não informa. */
  protegido: boolean | null;
  usado: number | null;
  cota: number | null;
}

export async function lerArmazenamento(): Promise<Armazenamento> {
  const s = typeof navigator === 'undefined' ? undefined : navigator.storage;
  let protegido: boolean | null = null;
  let usado: number | null = null;
  let cota: number | null = null;
  try { if (s?.persisted) protegido = await s.persisted(); } catch { /* nada */ }
  try {
    if (s?.estimate) {
      const e = await s.estimate();
      usado = e.usage ?? null;
      cota = e.quota ?? null;
    }
  } catch { /* nada */ }
  return { protegido, usado, cota };
}

/**
 * Pede ao navegador que não apague o que o aparelho guarda.
 *
 * O Chrome não pergunta nada a ninguém: ele dá ou não dá, pelas regras
 * dele — e dá ao aplicativo instalado. Pedir de novo quando já está dado
 * não custa nada, por isso as três portas chamam sem cerimônia.
 */
export async function protegerArmazenamento(): Promise<boolean | null> {
  const s = typeof navigator === 'undefined' ? undefined : navigator.storage;
  if (!s?.persist) return null;
  try {
    if (s.persisted && await s.persisted()) return true;
    return await s.persist();
  } catch { return null; }
}

/**
 * Chame uma vez, antes de desenhar a aplicação (como o `aplicarTema`).
 */
export function prepararInstalacao() {
  if (typeof window === 'undefined') return;
  lerOrigemDoIcone();
  aplicarManifesto(location.pathname);
  window.addEventListener('beforeinstallprompt', (e) => {
    pedido = e as PedidoDeInstalacao;
    avisar();
  });
  window.addEventListener('appinstalled', () => {
    pedido = null;
    instaladoAgora = true;
    void protegerArmazenamento();
    avisar();
  });
  if (rodandoComoApp()) void protegerArmazenamento();
}
